import React, { useEffect, useState, useMemo } from 'react';
import baseURL from '../../url';
import moneda from '../../moneda';
import { getConfig, fetchConfigFromDB } from '../../../utils/configService';
import { playSound } from '../../../utils/soundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faShoppingCart,
    faTrash,
    faPlus,
    faMinus,
    faMoneyBillWave,
    faQrcode,
    faUtensils,
    faShoppingBag,
    faCheckCircle,
    faPrint,
    faHistory,
    faTimes,
    faReceipt
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import './POS.css';

export default function POS() {
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [mesas, setMesas] = useState([]);
    const [pedidosAbiertos, setPedidosAbiertos] = useState([]);

    // Filtros de catálogo
    const [busqueda, setBusqueda] = useState('');
    const [categoriaActiva, setCategoriaActiva] = useState('todas');

    // Carrito POS
    const [posCart, setPosCart] = useState([]);
    const [tipoEntrega, setTipoEntrega] = useState('llevar'); // 'mesa' o 'llevar'
    const [idMesaSeleccionada, setIdMesaSeleccionada] = useState('');
    const [nombreCliente, setNombreCliente] = useState('Cliente Mostrador');
    const [notaPedido, setNotaPedido] = useState('');

    // Modal de Cobro
    const [modalCobro, setModalCobro] = useState(false);
    const [metodoPago, setMetodoPago] = useState('efectivo'); // 'efectivo', 'qr'
    const [montoRecibido, setMontoRecibido] = useState('');
    const [qrVerificado, setQrVerificado] = useState(false);
    const [procesandoPago, setProcesandoPago] = useState(false);

    // Modal / Drawer de Cobrar Pedidos Existentes
    const [vistaActiva, setVistaActiva] = useState('nueva_venta'); // 'nueva_venta', 'pedidos_pendientes'
    const [pedidoCobroDirecto, setPedidoCobroDirecto] = useState(null);

    // Ticket generado para imprimir
    const [ultimoTicket, setUltimoTicket] = useState(null);

    // Modalidad de precios (activada / desactivada)
    const [usarPrecios, setUsarPrecios] = useState(getConfig().usarPrecios !== 'no');

    useEffect(() => {
        cargarProductos();
        cargarCategorias();
        cargarMesas();
        cargarPedidos();

        fetchConfigFromDB().then(cfg => {
            if (cfg && cfg.usarPrecios !== undefined) {
                setUsarPrecios(cfg.usarPrecios !== 'no');
            }
        });

        const handleConfigChange = (e) => {
            if (e.detail && e.detail.usarPrecios !== undefined) {
                setUsarPrecios(e.detail.usarPrecios !== 'no');
            }
        };
        window.addEventListener('configUpdated', handleConfigChange);
        return () => window.removeEventListener('configUpdated', handleConfigChange);
    }, []);

    const cargarProductos = async () => {
        try {
            const res = await fetch(`${baseURL}/productosGet.php`);
            const data = await res.json();
            setProductos(data.productos || []);
        } catch (e) {
            console.error('Error al cargar productos:', e);
        }
    };

    const cargarCategorias = async () => {
        try {
            const res = await fetch(`${baseURL}/categoriaGet.php`);
            const data = await res.json();
            setCategorias(data.categorias || []);
        } catch (e) {
            console.error('Error al cargar categorías:', e);
        }
    };

    const cargarMesas = async () => {
        try {
            const res = await fetch(`${baseURL}/mesaGet.php`);
            const data = await res.json();
            setMesas(data.mesas || []);
        } catch (e) {
            console.error('Error al cargar mesas:', e);
        }
    };

    const cargarPedidos = async () => {
        try {
            const res = await fetch(`${baseURL}/pedidoGet.php`);
            const data = await res.json();
            const pendientes = (data.pedidos || []).filter(p => p.estado !== 'Pagado' && p.estado !== 'Rechazado');
            setPedidosAbiertos(pendientes.reverse());
        } catch (e) {
            console.error('Error al cargar pedidos:', e);
        }
    };

    // Agregar al carrito POS
    const agregarAlCarrito = (producto) => {
        try {
            const cfg = getConfig();
            const soundFile = cfg.sonidoAgregarProductoPOS || 'beep.mp3';
            playSound(soundFile);
        } catch (e) {
            console.error('Error al reproducir sonido en POS:', e);
        }

        setPosCart(prev => {
            const existente = prev.find(item => item.idProducto === producto.idProducto);
            if (existente) {
                return prev.map(item =>
                    item.idProducto === producto.idProducto
                        ? { ...item, cantidad: item.cantidad + 1 }
                        : item
                );
            }
            return [
                ...prev,
                {
                    idProducto: producto.idProducto,
                    titulo: producto.titulo,
                    precio: usarPrecios ? (Number(producto.precio) || 0) : 0,
                    cantidad: 1,
                    categoria: producto.categoria,
                    imagen: producto.imagen1 || producto.imagen2 || ''
                }
            ];
        });
    };

    const modificarPrecioItem = (idProducto, nuevoValor) => {
        setPosCart(prev => prev.map(item => {
            if (item.idProducto === idProducto) {
                return {
                    ...item,
                    precio: nuevoValor === '' ? '' : Math.max(0, parseFloat(nuevoValor) || 0)
                };
            }
            return item;
        }));
    };

    const modificarCantidad = (idProducto, delta) => {
        setPosCart(prev => {
            return prev.map(item => {
                if (item.idProducto === idProducto) {
                    const nuevaCant = item.cantidad + delta;
                    return nuevaCant > 0 ? { ...item, cantidad: nuevaCant } : null;
                }
                return item;
            }).filter(Boolean);
        });
    };

    const removerDelCarrito = (idProducto) => {
        setPosCart(prev => prev.filter(item => item.idProducto !== idProducto));
    };

    const vaciarCarrito = () => {
        setPosCart([]);
        setNombreCliente('Cliente Mostrador');
        setNotaPedido('');
    };

    const totalVenta = useMemo(() => {
        return posCart.reduce((acc, item) => {
            const p = parseFloat(item.precio) || 0;
            return acc + (p * item.cantidad);
        }, 0);
    }, [posCart]);

    const cambioCalculado = useMemo(() => {
        if (metodoPago !== 'efectivo') return 0;
        const recibido = parseFloat(montoRecibido) || 0;
        const total = pedidoCobroDirecto ? parseFloat(pedidoCobroDirecto.total) : totalVenta;
        return Math.max(0, recibido - total);
    }, [montoRecibido, metodoPago, pedidoCobroDirecto, totalVenta]);

    // Filtrar catálogo de productos
    const productosFiltrados = useMemo(() => {
        return productos.filter(p => {
            const coincideTexto = p.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
                                 p.categoria?.toLowerCase().includes(busqueda.toLowerCase());
            const coincideCat = categoriaActiva === 'todas' || p.categoria === categoriaActiva;
            return coincideTexto && coincideCat;
        });
    }, [productos, busqueda, categoriaActiva]);

    // Iniciar cobro
    const handleAbrirCobro = () => {
        if (posCart.length === 0) {
            toast.warn('Agrega al menos un producto al carrito');
            return;
        }
        if (tipoEntrega === 'mesa' && !idMesaSeleccionada) {
            toast.warn('Por favor selecciona una mesa');
            return;
        }
        setPedidoCobroDirecto(null);
        setMontoRecibido(totalVenta.toFixed(2));
        setQrVerificado(false);
        setModalCobro(true);
    };

    // Cobrar un pedido existente de una mesa
    const handleCobrarPedidoExistente = (pedido) => {
        setPedidoCobroDirecto(pedido);
        setMontoRecibido(parseFloat(pedido.total).toFixed(2));
        setQrVerificado(false);
        setModalCobro(true);
    };

    // Confirmar y registrar venta
    const handleConfirmarVenta = async () => {
        const totalCobro = pedidoCobroDirecto ? parseFloat(pedidoCobroDirecto.total) : totalVenta;
        const recibido = parseFloat(montoRecibido) || 0;

        if (metodoPago === 'efectivo' && totalCobro > 0 && recibido < totalCobro) {
            toast.error(`El monto recibido (${moneda} ${recibido}) es menor al total (${moneda} ${totalCobro})`);
            return;
        }

        if (metodoPago === 'qr' && !qrVerificado) {
            toast.error('Debe marcar la casilla de verificación tras confirmar el comprobante QR');
            return;
        }

        setProcesandoPago(true);

        try {
            if (pedidoCobroDirecto) {
                // Liquidar pedido existente
                const res = await fetch(`${baseURL}/pedidoPut.php?idPedido=${pedidoCobroDirecto.idPedido}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        estado: 'Pagado',
                        estadoPago: 'confirmado',
                        metodoPago: metodoPago,
                        montoRecibido: metodoPago === 'efectivo' ? recibido : totalCobro,
                        cambio: metodoPago === 'efectivo' ? cambioCalculado : 0
                    })
                });
                const data = await res.json();
                setProcesandoPago(false);

                if (data.mensaje) {
                    let mensajeExito = `Pedido #${pedidoCobroDirecto.idPedido} cobrado exitosamente.<br>Cambio a devolver: <b>${moneda} ${cambioCalculado.toFixed(2)}</b>`;
                    if (data.mesaLiberada) {
                        mensajeExito += `<br><br>🎉 <b>¡Mesa desocupada y disponible!</b> Se han completado todos los consumos de la mesa.`;
                    } else if (data.pedidosRestantes > 0) {
                        mensajeExito += `<br><br>⚠️ <b>Atención:</b> La mesa continúa ocupada porque aún tiene <b>${data.pedidosRestantes}</b> pedido(s) adicional(es) pendiente(s) de cobro.`;
                    }

                    Swal.fire({
                        title: '¡Cobro registrado!',
                        html: mensajeExito,
                        icon: 'success'
                    });
                    setUltimoTicket({
                        idPedido: pedidoCobroDirecto.idPedido,
                        total: totalCobro,
                        metodoPago,
                        montoRecibido: recibido,
                        cambio: cambioCalculado,
                        cliente: pedidoCobroDirecto.nombre,
                        fecha: new Date().toLocaleString()
                    });
                    setModalCobro(false);
                    cargarPedidos();
                    cargarMesas();
                } else {
                    toast.error(data.error || 'Error al registrar el cobro');
                }
            } else {
                // Registrar nueva venta POS directa
                const usuarioActual = JSON.parse(localStorage.getItem('usuario') || '{}');
                const idUsuario = usuarioActual.idUsuario || null;

                const productosGuardar = posCart.map(item => ({
                    ...item,
                    precio: parseFloat(item.precio) || 0
                }));

                const payload = {
                    idMesa: tipoEntrega === 'mesa' ? idMesaSeleccionada : null,
                    nombre: nombreCliente || 'Cliente Mostrador',
                    nota: notaPedido,
                    estado: 'Pagado', // Venta POS directa ya pagada
                    origen: 'pos',
                    tipoEntrega: tipoEntrega,
                    metodoPago: metodoPago,
                    estadoPago: 'confirmado',
                    montoRecibido: metodoPago === 'efectivo' ? recibido : totalCobro,
                    cambio: metodoPago === 'efectivo' ? cambioCalculado : 0,
                    idUsuario: idUsuario,
                    total: totalCobro,
                    productos: JSON.stringify(productosGuardar)
                };

                const res = await fetch(`${baseURL}/pedidoPost.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                const data = await res.json();
                setProcesandoPago(false);

                if (data.mensaje) {
                    Swal.fire({
                        title: '¡Venta completada!',
                        text: `Venta registrada con éxito. Cambio a devolver: ${moneda} ${cambioCalculado.toFixed(2)}`,
                        icon: 'success'
                    });
                    setUltimoTicket({
                        idPedido: data.idPedido || 'POS',
                        total: totalCobro,
                        metodoPago,
                        montoRecibido: recibido,
                        cambio: cambioCalculado,
                        cliente: nombreCliente,
                        productos: [...productosGuardar],
                        fecha: new Date().toLocaleString()
                    });
                    setModalCobro(false);
                    vaciarCarrito();
                    cargarPedidos();
                    cargarMesas();
                } else {
                    toast.error(data.error || 'Error al guardar la venta');
                }
            }
        } catch (e) {
            setProcesandoPago(false);
            console.error(e);
            toast.error('Error de red al procesar el cobro');
        }
    };

    // Liberar mesa desde el POS
    const handleLiberarMesaPOS = async (mesaObj) => {
        const pedidosMesa = pedidosAbiertos.filter(p => p.idMesa === mesaObj.idMesa);
        if (pedidosMesa.length > 0) {
            const total = pedidosMesa.reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0);
            Swal.fire({
                title: `Mesa ${mesaObj.mesa} con cobros pendientes`,
                html: `La <b>${mesaObj.mesa}</b> tiene <b>${pedidosMesa.length}</b> pedido(s) activo(s) o pendiente(s) de cobro (Total: <b>${moneda} ${total.toFixed(2)}</b>).<br><br>⚠️ Debes procesar el cobro de los pedidos pendientes antes de desocupar la mesa.`,
                icon: 'warning',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        const confirm = await Swal.fire({
            title: `¿Liberar ${mesaObj.mesa}?`,
            text: 'La mesa quedará desocupada y disponible para nuevos clientes.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, liberar mesa',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b'
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch(`${baseURL}/mesaLiberar.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idMesa: mesaObj.idMesa })
            });
            const data = await res.json();
            if (data.exito) {
                Swal.fire({
                    title: '¡Mesa liberada!',
                    text: `${mesaObj.mesa} ahora se encuentra disponible.`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                cargarMesas();
                cargarPedidos();
            } else {
                Swal.fire({
                    title: 'No se puede liberar',
                    text: data.mensaje || data.error,
                    icon: 'error'
                });
            }
        } catch (e) {
            toast.error('Error de red al liberar mesa');
        }
    };

    return (
        <div className="pos-container">
            <ToastContainer />

            {/* Sub-header de modos del POS */}
            <div className="pos-header-modes">
                <div className="pos-mode-tabs">
                    <button
                        className={`pos-mode-tab ${vistaActiva === 'nueva_venta' ? 'active' : ''}`}
                        onClick={() => setVistaActiva('nueva_venta')}
                    >
                        <FontAwesomeIcon icon={faReceipt} /> Nueva Venta
                    </button>
                    <button
                        className={`pos-mode-tab ${vistaActiva === 'pedidos_pendientes' ? 'active' : ''}`}
                        onClick={() => { setVistaActiva('pedidos_pendientes'); cargarPedidos(); }}
                    >
                        <FontAwesomeIcon icon={faHistory} /> Cobrar Mesas / Pedidos ({pedidosAbiertos.length})
                    </button>
                </div>
            </div>

            {vistaActiva === 'nueva_venta' ? (
                <div className="pos-main-layout">
                    {/* Panel Izquierdo: Catálogo y Categorías */}
                    <div className="pos-catalog-section">
                        {/* Buscador */}
                        <div className="pos-search-bar">
                            <FontAwesomeIcon icon={faSearch} className="pos-search-icon" />
                            <input
                                type="text"
                                placeholder="Buscar productos por nombre o categoría..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                            />
                            {busqueda && (
                                <button className="pos-search-clear" onClick={() => setBusqueda('')}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>

                        {/* Pestañas de categorías */}
                        <div className="pos-categories-pills">
                            <button
                                className={`cat-pill ${categoriaActiva === 'todas' ? 'active' : ''}`}
                                onClick={() => setCategoriaActiva('todas')}
                            >
                                Todas
                            </button>
                            {categorias.map(cat => (
                                <button
                                    key={cat.idCategoria || cat.categoria}
                                    className={`cat-pill ${categoriaActiva === cat.categoria ? 'active' : ''}`}
                                    onClick={() => setCategoriaActiva(cat.categoria)}
                                >
                                    {cat.categoria}
                                </button>
                            ))}
                        </div>

                        {/* Grid de productos */}
                        <div className="pos-products-grid">
                            {productosFiltrados.length === 0 ? (
                                <p className="pos-no-products">No se encontraron productos</p>
                            ) : (
                                productosFiltrados.map(prod => (
                                    <div
                                        key={prod.idProducto}
                                        className="pos-product-card"
                                        onClick={() => agregarAlCarrito(prod)}
                                    >
                                        <div className="pos-product-img-wrap">
                                            {prod.imagen1 ? (
                                                <img src={prod.imagen1} alt={prod.titulo} />
                                            ) : (
                                                <div className="pos-img-placeholder">
                                                    <FontAwesomeIcon icon={faUtensils} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="pos-product-info">
                                            <h4>{prod.titulo}</h4>
                                            {usarPrecios ? (
                                                <span className="pos-product-price">{moneda} {prod.precio}</span>
                                            ) : (
                                                <span className="pos-product-price-manual">Precio manual</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Panel Derecho: Ticket de Venta / Carrito POS */}
                    <div className="pos-ticket-section">
                        <div className="pos-ticket-header">
                            <div className="ticket-title">
                                <FontAwesomeIcon icon={faShoppingCart} />
                                <h3>Ticket de Venta</h3>
                            </div>
                            {posCart.length > 0 && (
                                <button className="btn-vaciar-pos" onClick={vaciarCarrito} title="Vaciar carrito">
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </div>

                        {/* Tipo de consumo: Mesa vs Para llevar */}
                        <div className="pos-delivery-selector">
                            <button
                                type="button"
                                className={`delivery-btn ${tipoEntrega === 'llevar' ? 'active' : ''}`}
                                onClick={() => setTipoEntrega('llevar')}
                            >
                                <FontAwesomeIcon icon={faShoppingBag} /> Para llevar
                            </button>
                            <button
                                type="button"
                                className={`delivery-btn ${tipoEntrega === 'mesa' ? 'active' : ''}`}
                                onClick={() => setTipoEntrega('mesa')}
                            >
                                <FontAwesomeIcon icon={faUtensils} /> En mesa
                            </button>
                        </div>

                        {tipoEntrega === 'mesa' && (
                            <div className="pos-mesa-select-box">
                                <label>Mesa de servicio:</label>
                                <select
                                    value={idMesaSeleccionada}
                                    onChange={(e) => setIdMesaSeleccionada(e.target.value)}
                                >
                                    <option value="">-- Seleccionar mesa --</option>
                                    {mesas.map(m => (
                                        <option key={m.idMesa} value={m.idMesa}>
                                            {m.mesa} ({m.estado})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="pos-client-inputs">
                            <input
                                type="text"
                                placeholder="Nombre del cliente"
                                value={nombreCliente}
                                onChange={(e) => setNombreCliente(e.target.value)}
                            />
                            <input
                                type="text"
                                placeholder="Nota u observación (opcional)"
                                value={notaPedido}
                                onChange={(e) => setNotaPedido(e.target.value)}
                            />
                        </div>

                        {/* Lista de productos en el ticket */}
                        <div className="pos-ticket-items">
                            {posCart.length === 0 ? (
                                <div className="pos-ticket-empty">
                                    <FontAwesomeIcon icon={faShoppingCart} />
                                    <p>Selecciona productos del catálogo para comenzar la venta</p>
                                </div>
                            ) : (
                                posCart.map(item => (
                                    <div key={item.idProducto} className="pos-ticket-item">
                                        <div className="ticket-item-left">
                                            <strong>{item.titulo}</strong>
                                            {usarPrecios ? (
                                                <span>{moneda} {(parseFloat(item.precio) || 0).toFixed(2)} c/u</span>
                                            ) : (
                                                <div className="ticket-item-manual-price">
                                                    <span className="lbl-manual">Precio ({moneda}):</span>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        className="input-manual-precio"
                                                        value={item.precio}
                                                        placeholder="0.00"
                                                        onChange={(e) => modificarPrecioItem(item.idProducto, e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="ticket-item-qty-controls">
                                            <button onClick={() => modificarCantidad(item.idProducto, -1)}>
                                                <FontAwesomeIcon icon={faMinus} />
                                            </button>
                                            <span className="ticket-qty">{item.cantidad}</span>
                                            <button onClick={() => modificarCantidad(item.idProducto, 1)}>
                                                <FontAwesomeIcon icon={faPlus} />
                                            </button>
                                        </div>
                                        <div className="ticket-item-subtotal">
                                            {moneda} {((parseFloat(item.precio) || 0) * item.cantidad).toFixed(2)}
                                        </div>
                                        <button
                                            className="ticket-item-remove"
                                            onClick={() => removerDelCarrito(item.idProducto)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Resumen y Botón de Cobro */}
                        <div className="pos-ticket-footer">
                            <div className="pos-total-row">
                                <span>Total a Pagar:</span>
                                <strong>{moneda} {totalVenta.toFixed(2)}</strong>
                            </div>

                            <button
                                className="pos-btn-cobrar"
                                onClick={handleAbrirCobro}
                                disabled={posCart.length === 0}
                            >
                                <FontAwesomeIcon icon={faMoneyBillWave} /> Cobrar {moneda} {totalVenta.toFixed(2)}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                /* Vista de Cobro de Pedidos Abiertos / Mesas */
                <div className="pos-open-orders-section">
                    <div className="open-orders-header">
                        <h3>📋 Mesas y Pedidos Pendientes de Cobro</h3>
                        <p>Selecciona un pedido para registrar su cobro y liberar la mesa correspondiente.</p>
                    </div>

                    {/* Mesas Ocupadas con control de liberación */}
                    {mesas.filter(m => m.estado !== 'libre').length > 0 && (
                        <div className="pos-occupied-tables-wrapper">
                            <h4 className="pos-occupied-title">🍽️ Mesas con Servicio Activo</h4>
                            <div className="pos-occupied-tables-grid">
                                {mesas.filter(m => m.estado !== 'libre').map(m => {
                                    const pedidosDeEstaMesa = pedidosAbiertos.filter(p => p.idMesa === m.idMesa);
                                    const totalMesa = pedidosDeEstaMesa.reduce((acc, p) => acc + (parseFloat(p.total) || 0), 0);

                                    return (
                                        <div key={m.idMesa} className="pos-table-status-card">
                                            <div className="pos-table-card-header">
                                                <strong>{m.mesa}</strong>
                                                <span className={`status-badge-small ${m.estado}`}>{m.estado}</span>
                                            </div>
                                            <div className="pos-table-card-body">
                                                <span>{pedidosDeEstaMesa.length} {pedidosDeEstaMesa.length === 1 ? 'pedido pendiente' : 'pedidos pendientes'}</span>
                                                <strong>{moneda} {totalMesa.toFixed(2)}</strong>
                                            </div>
                                            <div className="pos-table-card-actions">
                                                <button
                                                    type="button"
                                                    className="btn-liberar-mesa-pos"
                                                    onClick={() => handleLiberarMesaPOS(m)}
                                                    title="Liberar y desocupar mesa"
                                                >
                                                    <FontAwesomeIcon icon={faCheckCircle} /> Liberar Mesa
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="open-orders-grid">
                        {pedidosAbiertos.length === 0 ? (
                            <div className="no-open-orders">
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <p>No hay pedidos pendientes de cobro.</p>
                            </div>
                        ) : (
                            pedidosAbiertos.map(ped => {
                                const mesaObj = mesas.find(m => m.idMesa === ped.idMesa);
                                const mesaLabel = mesaObj ? mesaObj.mesa : (ped.idMesa ? `Mesa #${ped.idMesa}` : 'Para llevar');
                                return (
                                    <div key={ped.idPedido} className="open-order-card">
                                        <div className="open-card-top">
                                            <span className="order-chip">#{ped.idPedido}</span>
                                            <h4 className="mesa-chip">{mesaLabel}</h4>
                                            <span className={`status-badge-small ${ped.estado?.toLowerCase().replace(/\s+/g, '-')}`}>
                                                {ped.estado}
                                            </span>
                                        </div>

                                        <div className="open-card-client">
                                            <strong>Cliente:</strong> {ped.nombre || 'Mostrador'}
                                        </div>

                                        <div className="open-card-total">
                                            <span>Total:</span>
                                            <strong>{moneda} {parseFloat(ped.total).toFixed(2)}</strong>
                                        </div>

                                        <button
                                            className="btn-cobrar-open-order"
                                            onClick={() => handleCobrarPedidoExistente(ped)}
                                        >
                                            <FontAwesomeIcon icon={faMoneyBillWave} /> Cobrar Pedido
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* Modal de Pago / Cobro */}
            {modalCobro && (
                <div className="modal modal-pos-overlay">
                    <div className="modal-content modal-pos-content">
                        <div className="deFlexBtnsModal">
                            <h3>💵 Procesar Cobro</h3>
                            <span className="close" onClick={() => setModalCobro(false)}>&times;</span>
                        </div>

                        <div className="pos-modal-summary">
                            <span>Monto Total:</span>
                            <h2>{moneda} {(pedidoCobroDirecto ? parseFloat(pedidoCobroDirecto.total) : totalVenta).toFixed(2)}</h2>
                        </div>

                        {/* Selección de Método de Pago */}
                        <div className="pos-pay-methods">
                            <button
                                type="button"
                                className={`pay-method-btn ${metodoPago === 'efectivo' ? 'active' : ''}`}
                                onClick={() => setMetodoPago('efectivo')}
                            >
                                <FontAwesomeIcon icon={faMoneyBillWave} /> Efectivo
                            </button>
                            <button
                                type="button"
                                className={`pay-method-btn ${metodoPago === 'qr' ? 'active' : ''}`}
                                onClick={() => setMetodoPago('qr')}
                            >
                                <FontAwesomeIcon icon={faQrcode} /> Pago QR
                            </button>
                        </div>

                        {/* Bloque Efectivo */}
                        {metodoPago === 'efectivo' && (
                            <div className="pos-cash-block">
                                <label>Monto Recibido ({moneda}):</label>
                                <input
                                    type="number"
                                    step="0.5"
                                    value={montoRecibido}
                                    onChange={(e) => setMontoRecibido(e.target.value)}
                                    className="pos-input-recibido"
                                    autoFocus
                                />

                                <div className="pos-quick-cash-row">
                                    {[10, 20, 50, 100, 200].map(val => (
                                        <button
                                            key={val}
                                            type="button"
                                            className="quick-cash-btn"
                                            onClick={() => setMontoRecibido(val.toString())}
                                        >
                                            +{val}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        className="quick-cash-btn"
                                        onClick={() => setMontoRecibido((pedidoCobroDirecto ? parseFloat(pedidoCobroDirecto.total) : totalVenta).toFixed(2))}
                                    >
                                        Exacto
                                    </button>
                                </div>

                                <div className="pos-change-display">
                                    <span>Cambio a Devolver:</span>
                                    <strong className={cambioCalculado >= 0 ? 'cambio-positivo' : 'cambio-negativo'}>
                                        {moneda} {cambioCalculado.toFixed(2)}
                                    </strong>
                                </div>
                            </div>
                        )}

                        {/* Bloque Pago QR */}
                        {metodoPago === 'qr' && (
                            <div className="pos-qr-block">
                                <div className="pos-qr-notice">
                                    <p>📱 Solicite al cliente escanear el código QR del negocio y realizar la transferencia por el total exacto.</p>
                                </div>

                                <label className="pos-qr-verify-check">
                                    <input
                                        type="checkbox"
                                        checked={qrVerificado}
                                        onChange={(e) => setQrVerificado(e.target.checked)}
                                    />
                                    <span>He verificado el comprobante bancario del cliente en mi dispositivo o extracto</span>
                                </label>
                            </div>
                        )}

                        <div className="pos-modal-actions">
                            <button
                                className="pos-btn-cancelar-cobro"
                                onClick={() => setModalCobro(false)}
                                disabled={procesandoPago}
                            >
                                Cancelar
                            </button>
                            <button
                                className="pos-btn-confirmar-cobro"
                                onClick={handleConfirmarVenta}
                                disabled={procesandoPago}
                            >
                                {procesandoPago ? 'Registrando...' : 'Confirmar Cobro y Facturar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
